import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Lock, TrendingUp } from "lucide-react";

/**
 * PollWidget — displays a "Will it Land?" style poll for a launch.
 * Two variants:
 *   - variant="compact" → single-line chip for a card (question + %s + status)
 *   - variant="full"    → block widget with vote buttons, breakdown, admin resolve
 *
 * Props:
 *   - poll: initial poll object from GET /polls/by-launch/{launch_id} (may be null)
 *   - launchId: string (used to refetch after mutations)
 *   - variant: "compact" | "full"
 */
export default function PollWidget({ poll: initial, launchId, variant = "full" }) {
  const [poll, setPoll] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/polls/by-launch/${launchId}`);
      setPoll(r.data?.poll || null);
    } catch (_) {}
  }, [launchId]);

  useEffect(() => {
    if (!initial) refresh();
  }, [initial, refresh]);

  if (!poll) return null;

  const t = poll.tallies || { yes: 0, no: 0, total: 0 };
  const yesPct = t.total ? Math.round((t.yes / t.total) * 100) : 0;
  const noPct = t.total ? 100 - yesPct : 0;
  const locked = poll.is_locked;
  const resolved = poll.outcome === "yes" || poll.outcome === "no";

  const vote = async (choice) => {
    if (!user) {
      toast.info("Log in to cast your prediction");
      return;
    }
    if (locked) return;
    setBusy(true);
    try {
      const r = await api.post(`/polls/${poll.id}/vote`, { choice });
      setPoll(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed");
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (outcome) => {
    if (!window.confirm(`Resolve this poll as "${outcome.toUpperCase()}"?`)) return;
    setBusy(true);
    try {
      const r = await api.put(`/polls/${poll.id}/resolve`, { outcome });
      setPoll(r.data);
      toast.success("Poll resolved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    } finally {
      setBusy(false);
    }
  };

  // ---------- COMPACT ----------
  if (variant === "compact") {
    return (
      <div
        className="border border-[color:var(--sat-border-bright)] bg-[color:var(--sat-surface-2)] px-3 py-2 flex items-center gap-3 text-[11px] font-mono"
        data-testid="poll-chip"
        onClick={(e) => e.preventDefault()}
      >
        <span className="sat-cyan tracking-widest">{poll.kind === "landing" ? "WILL IT LAND?" : "WILL IT LAUNCH?"}</span>
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <span className="text-[color:var(--sat-cyan)] tabular-nums" data-testid="poll-chip-yes">{yesPct}%</span>
          <div className="flex-1 h-1 bg-[color:var(--sat-border)] overflow-hidden">
            <div className="h-full bg-[color:var(--sat-cyan)] transition-all" style={{ width: `${yesPct}%` }} />
          </div>
          <span className="text-[color:var(--sat-text-dim)] tabular-nums" data-testid="poll-chip-total">{t.total} VOTES</span>
        </div>
        {resolved && (
          <span className={`px-1.5 py-0.5 border font-bold ${poll.outcome === "yes" ? "border-[color:var(--sat-cyan)] text-[color:var(--sat-cyan)]" : "border-[color:var(--sat-live)] text-[color:var(--sat-live)]"}`}>
            {poll.outcome === "yes" ? "✓ LANDED" : "✗ FAILED"}
          </span>
        )}
      </div>
    );
  }

  // ---------- FULL ----------
  const myVote = poll.my_vote;
  const wasCorrect = resolved && myVote && myVote === poll.outcome;
  const wasWrong = resolved && myVote && myVote !== poll.outcome;

  return (
    <section
      className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-5"
      data-testid="poll-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={13} className="sat-cyan" />
        <span className="sat-label sat-cyan">POLL // PREDICTION</span>
        {locked && !resolved && (
          <span className="sat-label ml-auto flex items-center gap-1 text-[color:var(--sat-text-dim)]">
            <Lock size={11} /> VOTING CLOSED
          </span>
        )}
        {resolved && (
          <span className={`sat-label ml-auto flex items-center gap-1 ${poll.outcome === "yes" ? "sat-cyan" : "text-[color:var(--sat-live)]"}`}>
            RESOLVED
          </span>
        )}
      </div>

      <h3 className="font-display font-bold text-xl mb-4" data-testid="poll-question">{poll.question}</h3>

      {/* Vote buttons OR outcome banner */}
      {!resolved && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={() => vote("yes")}
            disabled={busy || locked}
            data-testid="poll-vote-yes"
            className={`p-4 border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              myVote === "yes"
                ? "border-[color:var(--sat-cyan)] bg-[color:var(--sat-cyan-soft)] sat-box-glow"
                : "border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-cyan)]"
            }`}
          >
            <div className="font-display font-bold text-2xl text-[color:var(--sat-cyan)]">YES</div>
            <div className="sat-label text-[color:var(--sat-text-dim)] mt-1">{poll.kind === "landing" ? "LANDS" : "SUCCEEDS"}</div>
            {myVote === "yes" && <div className="sat-label sat-cyan mt-2">✓ YOUR PICK</div>}
          </button>
          <button
            onClick={() => vote("no")}
            disabled={busy || locked}
            data-testid="poll-vote-no"
            className={`p-4 border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              myVote === "no"
                ? "border-[color:var(--sat-live)] bg-[color:var(--sat-live)]/10"
                : "border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-live)]"
            }`}
          >
            <div className="font-display font-bold text-2xl text-[color:var(--sat-live)]">NO</div>
            <div className="sat-label text-[color:var(--sat-text-dim)] mt-1">{poll.kind === "landing" ? "LOST" : "FAILS"}</div>
            {myVote === "no" && <div className="sat-label text-[color:var(--sat-live)] mt-2">✓ YOUR PICK</div>}
          </button>
        </div>
      )}

      {resolved && (
        <div
          className={`p-4 mb-4 border-2 ${
            poll.outcome === "yes"
              ? "border-[color:var(--sat-cyan)] bg-[color:var(--sat-cyan-soft)]"
              : "border-[color:var(--sat-live)] bg-[color:var(--sat-live)]/10"
          }`}
          data-testid="poll-outcome"
        >
          <div className="flex items-center gap-2">
            {poll.outcome === "yes"
              ? <CheckCircle2 size={16} className="sat-cyan" />
              : <XCircle size={16} className="text-[color:var(--sat-live)]" />}
            <span className={`font-display font-bold text-lg ${poll.outcome === "yes" ? "sat-cyan" : "text-[color:var(--sat-live)]"}`}>
              OUTCOME: {poll.outcome === "yes" ? (poll.kind === "landing" ? "LANDED" : "SUCCESS") : (poll.kind === "landing" ? "LOST" : "FAILURE")}
            </span>
          </div>
          {wasCorrect && (
            <div className="sat-label sat-cyan mt-2" data-testid="poll-was-correct">
              ✓ YOU WERE RIGHT — GOOD PREDICTION, PILOT.
            </div>
          )}
          {wasWrong && (
            <div className="sat-label text-[color:var(--sat-live)] mt-2" data-testid="poll-was-wrong">
              ✗ BETTER LUCK NEXT TIME, PILOT.
            </div>
          )}
          {resolved && !myVote && (
            <div className="sat-label text-[color:var(--sat-text-dim)] mt-2">
              YOU DID NOT VOTE ON THIS POLL.
            </div>
          )}
        </div>
      )}

      {/* Live tallies */}
      <div className="space-y-2" data-testid="poll-tallies">
        <div>
          <div className="flex justify-between text-[11px] font-mono mb-1">
            <span className="sat-cyan">YES · {yesPct}%</span>
            <span className="text-[color:var(--sat-text-dim)] tabular-nums">{t.yes}</span>
          </div>
          <div className="h-2 bg-[color:var(--sat-surface-2)] border border-[color:var(--sat-border)]">
            <div className="h-full bg-[color:var(--sat-cyan)] transition-all duration-500" style={{ width: `${yesPct}%` }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-[11px] font-mono mb-1">
            <span className="text-[color:var(--sat-live)]">NO · {noPct}%</span>
            <span className="text-[color:var(--sat-text-dim)] tabular-nums">{t.no}</span>
          </div>
          <div className="h-2 bg-[color:var(--sat-surface-2)] border border-[color:var(--sat-border)]">
            <div className="h-full bg-[color:var(--sat-live)] transition-all duration-500" style={{ width: `${noPct}%` }} />
          </div>
        </div>
        <div className="pt-2 sat-label text-[color:var(--sat-text-dim)]">
          {t.total} VOTE{t.total === 1 ? "" : "S"} · {poll.launch_provider || "—"}
        </div>
      </div>

      {/* Admin controls */}
      {isAdmin && !resolved && (
        <div className="mt-4 pt-4 border-t border-[color:var(--sat-border-bright)]">
          <div className="sat-label mb-2 text-[color:var(--sat-text-dim)]">// ADMIN · RESOLVE OUTCOME</div>
          <div className="flex gap-2">
            <button onClick={() => resolve("yes")} disabled={busy}
                    className="sat-btn sat-btn-solid flex-1"
                    data-testid="poll-admin-resolve-yes">✓ YES · {poll.kind === "landing" ? "LANDED" : "SUCCESS"}</button>
            <button onClick={() => resolve("no")} disabled={busy}
                    className="sat-btn flex-1 !border-[color:var(--sat-live)] !text-[color:var(--sat-live)]"
                    data-testid="poll-admin-resolve-no">✗ NO · {poll.kind === "landing" ? "LOST" : "FAILURE"}</button>
          </div>
        </div>
      )}
    </section>
  );
}

/** PollListItem — used on the LaunchesPage POLLS tab. */
export function PollListItem({ poll }) {
  const t = poll.tallies || { yes: 0, no: 0, total: 0 };
  const yesPct = t.total ? Math.round((t.yes / t.total) * 100) : 0;
  const resolved = poll.outcome === "yes" || poll.outcome === "no";
  return (
    <Link
      to={`/launches/${poll.launch_id}`}
      data-testid="poll-list-item"
      className="sat-surface sat-brackets group block p-4 md:p-5 border border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-cyan)] transition-colors"
    >
      <div className="flex items-start gap-4">
        {poll.launch_image && (
          <img src={poll.launch_image} alt="" loading="lazy"
               className="w-24 h-24 md:w-28 md:h-28 object-cover border border-[color:var(--sat-border)] flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="sat-label sat-cyan">{poll.kind === "landing" ? "WILL IT LAND?" : "WILL IT LAUNCH?"}</span>
            {poll.launch_provider && <span className="sat-label text-[color:var(--sat-text-dim)]">// {poll.launch_provider}</span>}
            {resolved && (
              <span className={`sat-label px-1.5 py-0.5 border ${poll.outcome === "yes" ? "border-[color:var(--sat-cyan)] sat-cyan" : "border-[color:var(--sat-live)] text-[color:var(--sat-live)]"}`}>
                {poll.outcome === "yes" ? "✓ LANDED" : "✗ FAILED"}
              </span>
            )}
            {poll.is_locked && !resolved && (
              <span className="sat-label px-1.5 py-0.5 border border-[color:var(--sat-text-dim)] text-[color:var(--sat-text-dim)] flex items-center gap-1">
                <Lock size={9} /> LOCKED
              </span>
            )}
          </div>
          <h3 className="font-display font-bold text-lg group-hover:text-[color:var(--sat-cyan)] transition-colors truncate">
            {poll.launch_name}
          </h3>
          <div className="mt-3 space-y-1.5">
            <div className="flex justify-between text-[11px] font-mono">
              <span className="sat-cyan tabular-nums">YES · {yesPct}%</span>
              <span className="text-[color:var(--sat-text-dim)] tabular-nums">{t.total} VOTE{t.total === 1 ? "" : "S"}</span>
            </div>
            <div className="h-1.5 bg-[color:var(--sat-surface-2)] border border-[color:var(--sat-border)]">
              <div className="h-full bg-[color:var(--sat-cyan)]" style={{ width: `${yesPct}%` }} />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
